import React, { useEffect } from 'react';
import { ContentsData, getOriginFromUrl, isPlayerHost } from '../helpers';

/**
 * 젭스플레이어 iframe에 accessToken을 주입하는 로직을 관리하는 훅.
 * @param contentsData 현재 표시 중인 콘텐츠(대상 URL 판별에 사용)
 * @param iframeRef 콘텐츠 iframe 참조
 * @description
 * - 토큰 초기값은 같은 오리진(jebs.kr)의 localStorage에서 직접 읽어, 오프너의 postMessage
 *   유실이나 새로고침 후에도 유지된다.
 * - 콘텐츠 URL/토큰이 바뀌면 자동으로 전송을 시도한다.
 */
export const usePlayerToken = (
  contentsData: ContentsData | undefined,
  iframeRef: React.RefObject<HTMLIFrameElement>,
) => {
  const [todoAccessToken, setTodoAccessToken] = React.useState(
    () => localStorage.getItem('accessToken') ?? '',
  );

  /**
   * 젭스플레이어 iframe에 accessToken을 postMessage로 전송한다.
   * @description
   * 아래 조건을 모두 만족할 때만 전송하고, 그렇지 않으면 조용히 건너뛴다.
   * - 현재 콘텐츠 URL이 존재하고 젭스플레이어 host인 경우
   * - iframe window와 accessToken이 존재하는 경우
   * - iframe이 실제로 player 도메인(cross-origin)까지 로드를 마친 경우
   *   (about:blank 초기 상태나 리다이렉트 중이면 아직 준비 안 된 것으로 보고 건너뛴다)
   */
  const postTokenToPlayer = React.useCallback(() => {
    const targetUrl = contentsData?.contentsUrl;
    const iframeWindow = iframeRef.current?.contentWindow;

    if (
      !targetUrl ||
      !iframeWindow ||
      !todoAccessToken ||
      !isPlayerHost(targetUrl)
    ) {
      return;
    }

    const targetOrigin = getOriginFromUrl(targetUrl);
    if (!targetOrigin) return;

    // iframe이 targetOrigin으로 실제 로드됐는지 확인한다.
    // cross-origin(player 도메인)이면 location 접근 시 SecurityError → 정상, postMessage 진행.
    // same-origin(about:blank 초기 상태 또는 localhost 리다이렉트)이면 접근 가능 → 아직 준비 안 됨, 건너뜀.
    try {
      // same-origin이면 접근 가능 → iframe이 아직 player 도메인에 없음 (about:blank 또는 리다이렉트)
      // (접근 자체가 목적이므로 void로 값을 버린다. cross-origin이면 여기서 SecurityError가 난다)
      void iframeWindow.location.origin;
      return;
    } catch {
      // cross-origin SecurityError → player가 targetOrigin에 정상 로드됨
    }

    iframeWindow.postMessage(
      {
        type: 'JEBS_ACCESS_TOKEN',
        accessToken: todoAccessToken,
      },
      targetOrigin,
    );
  }, [contentsData?.contentsUrl, todoAccessToken, iframeRef]);

  /**
   * 항상 최신 postTokenToPlayer를 가리키는 ref.
   * @description
   * message 핸들러는 마운트 시 한 번만 등록되어 첫 렌더의 클로저에 갇히므로,
   * 최신 상태(contentsData/todoAccessToken)를 반영하려면 ref를 통해 호출해야 한다.
   */
  const postTokenToPlayerRef = React.useRef(postTokenToPlayer);
  postTokenToPlayerRef.current = postTokenToPlayer;

  // 콘텐츠 URL 또는 토큰이 바뀌면 전송을 시도한다.
  useEffect(() => {
    postTokenToPlayer();
  }, [postTokenToPlayer]);

  return {
    todoAccessToken,
    setTodoAccessToken,
    postTokenToPlayer,
    postTokenToPlayerRef,
  };
};
